import React, { useEffect, useState } from 'react'
import { GeoJSON } from 'react-leaflet'
import { loadCountriesGeoJSON } from '../../../utils/geojson'

/**
 * Country layer component that colors countries based on setup count
 */
export const CountrySetupsLayer = ({ countries }) => {
  const [geoJsonData, setGeoJsonData] = useState(null)

  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const data = await loadCountriesGeoJSON()
        setGeoJsonData(data)
      } catch (error) {
        console.error('Error loading GeoJSON data:', error)
      }
    }

    loadGeoJSON()
  }, [])

  // Create a lookup map of total installed capacity (MW) by country code
  const capacityLookup = React.useMemo(() => {
    if (!countries) return {}

    const lookup = {}
    countries.forEach(country => {
      const totalMw = (country.solar?.mw || 0) + (country.wind?.mw || 0)
      lookup[country.code] = totalMw
      // Also map common variations
      if (country.code === 'GB') lookup['UK'] = totalMw
      if (country.code === 'US') lookup['USA'] = totalMw
    })
    return lookup
  }, [countries])

  // Highest total capacity across all countries, used to normalize the color
  const maxCapacity = React.useMemo(() => {
    const values = Object.values(capacityLookup)
    return values.length ? Math.max(...values) : 0
  }, [capacityLookup])

  // Get color based on total installed capacity (MW)
  const getCapacityColor = (totalMw, maxMw) => {
    if (!totalMw || !maxMw) return null // No color without capacity

    // Square-root scale: capacity is heavily skewed (a few huge countries, many
    // small ones). Sqrt keeps large differences clearly visible (unlike a log
    // scale, which makes everything look similar) while still giving small
    // countries a distinguishable tint (unlike a linear scale, which washes
    // them out to near-white).
    const normalized = Math.min(
      Math.max(Math.sqrt(totalMw) / Math.sqrt(maxMw), 0),
      1,
    )

    // Blue color scale from light to dark
    const lightBlue = [173, 216, 230] // lightblue
    const darkBlue = [25, 25, 112] // midnightblue

    const r = Math.round(
      lightBlue[0] + (darkBlue[0] - lightBlue[0]) * normalized,
    )
    const g = Math.round(
      lightBlue[1] + (darkBlue[1] - lightBlue[1]) * normalized,
    )
    const b = Math.round(
      lightBlue[2] + (darkBlue[2] - lightBlue[2]) * normalized,
    )

    return `rgb(${r}, ${g}, ${b})`
  }

  // Style function for GeoJSON features
  const getFeatureStyle = feature => {
    const props = feature.properties

    // Use the correct property names from your GeoJSON
    const countryCode = props.ISO // Your GeoJSON uses 'ISO' field
    const totalMw = capacityLookup[countryCode]

    const color = getCapacityColor(totalMw, maxCapacity)

    if (!color) {
      // No setups - return invisible/transparent style
      return {
        fillColor: 'transparent',
        color: 'transparent',
        weight: 0,
        fillOpacity: 0,
        opacity: 0,
      }
    }

    return {
      fillColor: color,
      weight: 1,
      opacity: 0.8,
      color: '#2c3e50',
      fillOpacity: 0.6,
    }
  }

  if (!geoJsonData) return null

  return (
    <GeoJSON
      key={`country-setups-${countries?.length || 0}`}
      data={geoJsonData}
      style={getFeatureStyle}
    />
  )
}
